import {
  ActionIcon,
  Button,
  Group,
  Input,
  InputWrapper,
  Skeleton,
  Stack,
  Title,
} from "@mantine/core";
import { MonthPickerInput } from "@mantine/dates";
import { useLocalStorage } from "@mantine/hooks";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CarsDonutChart } from "../CarsDonutChart";

import { Carousel, EmptyState, TextSerif } from "@/components/elements";
import { IconCalendar, IconCurrencyEuro, IconPercentage } from "@/components/icons";
import { CurrencyCode, defaultCurrency } from "@/lib/currency";
import { usePlansUser } from "@/services/plans/get-user-plan";
import { Currency } from "@/types/currency";
import { getValuePool } from "@/utils/getValuePool";

import type { Car } from "@/types/car";
import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";
import type { CarsDonutChartProps } from "../CarsDonutChart";

export type GarageAggregationViewProps = {
  cars?: Car[];
  defaultType?: CarsDonutChartProps["valueType"];
  getPath?: (carId: string) => string;
  loading?: boolean;
  onBreakdownChange?: (date?: string) => void;
} & MantineStyleProps;

export const GarageAggregationView: FC<GarageAggregationViewProps> = ({
  cars = [],
  defaultType = "percent",
  getPath,
  loading = false,
  onBreakdownChange,
  ...props
}) => {
  const { t } = useTranslation();
  const [valueType, setValueType] = useState(defaultType);
  const today = dayjs().format();
  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  const { data: plans } = usePlansUser();

  function handleBreakdownChange(date?: string | null) {
    onBreakdownChange?.(date || undefined);
  }

  const hasInitialData = cars.length > 0;

  const isNotFreePlan = plans?.name !== "starter";

  const slideSize = { base: "calc(100%/1)", sm: isNotFreePlan ? "calc(100%/3)" : "calc(100%/2)" };

  const slidesArray = [
    ...(isNotFreePlan
      ? [
          <CarsDonutChart
            key="by_condition"
            color="violet"
            getPath={getPath}
            groupBy={({ condition }) =>
              condition
                ? t(`car.conditions.${condition}`, condition)
                : t("car.conditions.undefined")
            }
            title={t("garage.by_condition")}
            valueType={valueType}
            values={cars}
          />,
        ]
      : []),
    <CarsDonutChart
      key="by_make"
      color="orange"
      getPath={getPath}
      groupBy={({ make }) => make}
      title={t("garage.by_make")}
      valueType={valueType}
      values={cars}
    />,
    <CarsDonutChart
      key="by_decades"
      color="green"
      getPath={getPath}
      groupBy={({ production_year }) => {
        if (production_year < 1950) return t("car.decades.1940");
        if (production_year < 1960) return t("car.decades.1950");
        if (production_year < 1970) return t("car.decades.1960");
        if (production_year < 1980) return t("car.decades.1970");
        if (production_year < 1990) return t("car.decades.1980");
        if (production_year < 2000) return t("car.decades.1990");
        return t("car.decades.2000");
      }}
      title={t("garage.by_decades")}
      valueType={valueType}
      values={cars}
    />,
    <CarsDonutChart
      key="by_value"
      color="blue"
      getPath={getPath}
      groupBy={(_, value) => {
        return getValuePool(value);
      }}
      title={t("garage.by_value")}
      valueType={valueType}
      values={cars}
    />,
    <CarsDonutChart
      key="by_model"
      color="yellow"
      getPath={getPath}
      hideBadge
      title={t("garage.by_model")}
      valueType={valueType}
      values={cars}
    />,
  ];

  return (
    <Skeleton visible={loading}>
      <Stack {...props}>
        <Group justify="space-between">
          <Stack gap="xs">
            <Title order={3}>{t("garage.portfolio")}</Title>
            <TextSerif fz="sm">{t("garage.portfolio_description")}</TextSerif>
          </Stack>
          <Group gap="md">
            {onBreakdownChange && (
              <MonthPickerInput
                defaultValue={today}
                label={t("garage.breakdown")}
                leftSection={<IconCalendar />}
                maxDate={new Date()}
                onChange={handleBreakdownChange}
                valueFormat="MMM YYYY"
              />
            )}

            <InputWrapper>
              <Input.Label>{t("garage.by")}</Input.Label>
              <Button.Group>
                <Button
                  px="xs"
                  onClick={() => setValueType("percent")}
                  variant={valueType === "percent" ? "light" : "subtle"}
                >
                  <IconPercentage />
                </Button>

                <Button
                  px="xs"
                  onClick={() => setValueType(currency.code)}
                  variant={valueType !== "percent" ? "light" : "subtle"}
                >
                  <ActionIcon variant="transparent">
                    {currency.code === CurrencyCode.EUR ? <IconCurrencyEuro /> : currency.symbol}
                  </ActionIcon>
                </Button>
              </Button.Group>
            </InputWrapper>
          </Group>
        </Group>
        {hasInitialData ? (
          cars.length > 0 ? (
            <Carousel
              emblaOptions={{ align: "start", containScroll: "trimSnaps", slidesToScroll: "auto" }}
              pt="xl"
              slideGap="xl"
              slideProps={{ m: 0 }}
              slideSize={slideSize}
              withControls
              slides={slidesArray}
            />
          ) : (
            <EmptyState type="noResults" text={t("common.noResults")} />
          )
        ) : (
          <EmptyState type="noData" text={t("common.noData")} />
        )}
      </Stack>
    </Skeleton>
  );
};
